/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Icon from '@/components/atoms/icon/icon';
import CalendarSliderTile, {
  CalendarSliderTileProps,
} from '@/components/molecules/calendar-slider-tile/calendar-slider-tile';
import validateSameDate from '@/libraries/validators/validate-same-date';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import styles from './calendar-slider.module.scss';

export interface CalendarSliderProps {
  /** The class name to apply */
  className?: string;
  /** The list of dates to show */
  dates?: CalendarSliderTileProps[];
  /** Indicates whether the calendar slider is in a loading state */
  isLoading?: boolean;
  /** An event that is triggered on change */
  onChange?: (selectedDate: Date, isDisabled: boolean) => void;
  /** The initially selected date */
  selectedDate?: Date;
}

const findDateIndex = (
  isLoading: boolean,
  dates: CalendarSliderTileProps[],
  selectedDate?: Date,
): number => {
  if (isLoading) {
    return Math.round(dates.length);
  }

  return selectedDate
    ? dates.findIndex(
        dateItem =>
          dateItem.date && validateSameDate(dateItem.date, selectedDate),
      )
    : 0;
};

const CalendarSlider = ({
  className,
  dates = [],
  isLoading = false,
  onChange,
  selectedDate,
}: CalendarSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(
    findDateIndex(isLoading, dates, selectedDate),
  );
  const swiperRef = useRef<SwiperInstance | null>(null);

  const handleClickSlide = (
    index: number,
    date: Date,
    isDisabled: boolean = false,
  ) => {
    setCurrentIndex(index);
    onChange?.(date, isDisabled);
  };

  const handleClickNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handleClickPrevious = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  useEffect(() => {
    setCurrentIndex(findDateIndex(isLoading, dates, selectedDate));
  }, [dates, isLoading, selectedDate]);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(currentIndex);
    }
  }, [currentIndex]);

  const renderSlides = () => {
    if (isLoading) {
      return Array.from({ length: 24 }, (_, index) => (
        <SwiperSlide key={index}>
          <CalendarSliderTile isLoading />
        </SwiperSlide>
      ));
    }

    return dates.map((dateItem, dateIndex) => (
      <SwiperSlide
        key={dateIndex}
        className={styles.dateItem}
        onClick={() =>
          dateItem.date &&
          handleClickSlide(
            dateIndex,
            dateItem.date,
            dateItem.disabled as boolean,
          )
        }
      >
        <CalendarSliderTile {...dateItem} active={dateIndex === currentIndex} />
      </SwiperSlide>
    ));
  };

  return (
    <div className={classNames(styles.component, className)}>
      <div className={styles.gradient}>
        <Swiper
          breakpoints={{
            [parseInt(breakpoints.tablet)]: {
              spaceBetween: 12,
            },
          }}
          centeredSlides={true}
          className={styles.swiper}
          initialSlide={currentIndex}
          keyboard={true}
          onSwiper={swiper => (swiperRef.current = swiper)}
          slidesPerView="auto"
          spaceBetween={6}
          speed={500}
          wrapperClass={styles.swiperWrapper}
        >
          {renderSlides()}
        </Swiper>
      </div>

      <button
        className={classNames(styles.button, styles.isPrevious)}
        onClick={handleClickPrevious}
      >
        <Icon name="ChevronLeft" size={16} />
      </button>

      <button
        className={classNames(styles.button, styles.isNext)}
        onClick={handleClickNext}
      >
        <Icon name="ChevronRight" size={16} />
      </button>
    </div>
  );
};

CalendarSlider.displayName = 'CalendarSlider';

export default CalendarSlider;
